import React, { FC, ImgHTMLAttributes, useState, useEffect, useCallback } from 'react';
import { ReactComponent as XIcon } from '../assets/x-icon.svg';
import SkeletonLoader from './SkeletonLoader';

interface AvatarGifProps extends ImgHTMLAttributes<HTMLImageElement> {
    isLoading?: boolean;
    onMouseDown?: (e: React.MouseEvent) => void;
    onTouchStart?: (e: React.TouchEvent) => void;
    showCloseButton?: boolean;
}

const AvatarGif:FC<AvatarGifProps> = React.memo(({ className, height, width, onClick, isLoading, onMouseDown, onTouchStart, showCloseButton = false }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [hoverTimeout, setHoverTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);

    // Cleanup timeout on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            if (hoverTimeout) {
                clearTimeout(hoverTimeout);
            }
        };
    }, [hoverTimeout]);

    const handleRemoveChat = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        let currentElement = e.currentTarget as HTMLElement;

        while (currentElement && currentElement.parentElement && currentElement.parentElement !== document.body) {
            currentElement = currentElement.parentElement;
            if (currentElement.id && currentElement.id.endsWith('-container')) {
                currentElement.remove();
                return;
            }
        }
    }, []);

    const handleMouseEnter = useCallback(() => {
        if (hoverTimeout) {
            clearTimeout(hoverTimeout);
            setHoverTimeout(null);
        }
        setIsHovered(true);
    }, [hoverTimeout]);

    const handleMouseLeave = useCallback(() => {
        const timeout = setTimeout(() => {
            setIsHovered(false);
        }, 400); // 400ms delay before hiding the X icon
        setHoverTimeout(timeout);
    }, []);

    return (
        <div 
            className="avatar-gif-container"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            data-testid="avatar-gif-container"
        >
            {isLoading ? (
                <SkeletonLoader type="avatar" width={width} height={height} data-testid="avatar-skeleton-loader" />
            ) : (
                <>
                    <img
                        className={className}
                        height={height}
                        width={width}
                        src="https://xtm-assistant.xtrf.eu/avatarGifIcon.gif"
                        onClick={onClick}
                        alt="GIF"
                        draggable={false}
                        data-testid="avatar-gif-image"
                        style={{
                            willChange: 'transform',
                            backfaceVisibility: 'hidden',
                            display: 'block',
                            boxShadow: '0 0 14.4px 0 rgba(120, 63, 194, 0.50)',
                            borderRadius: '50%',
                            transition: 'opacity 0.3s cubic-bezier(0.4, 0.0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)'
                        }}
                    />
                    {showCloseButton && isHovered && (
                        <div 
                            className="avatar-remove-icon animated-fade-in" 
                            onClick={handleRemoveChat}
                            title="Hide until page reload."
                            onMouseEnter={handleMouseEnter}
                            data-testid="avatar-remove-icon"
                        >
                            <XIcon data-testid="avatar-x-icon" />
                        </div>
                    )}
                </>
            )}
        </div>
    );
});
export default AvatarGif
